import Component from '@glimmer/component';
import EditorJS from '@editorjs/editorjs';
import Header from '@editorjs/header';
import List from '@editorjs/list';
import { modifier } from 'ember-modifier';
import { tracked } from '@glimmer/tracking';
import { t } from 'ember-intl';
import type { OutputData, ToolConstructable } from '@editorjs/editorjs';
import type LpnPage from '../../models/lpn-page';

const MAX_CHAR_COUNT = 365;

const extractPlainText = (data?: OutputData): string => {
  if (!data?.blocks?.length) return '';
  return data.blocks
    .map((block) => {
      const blockData = block?.data as Record<string, unknown> | undefined;
      if (!blockData) return '';
      if (typeof blockData.text === 'string') {
        return blockData.text;
      }
      if (Array.isArray(blockData.items)) {
        return blockData.items.filter((item) => typeof item === 'string').join(' ');
      }
      return '';
    })
    .join(' ')
    .replace(/<[^>]*>/g, '')
    .trim();
};

const getCharacterCount = (data?: OutputData): number => {
  return extractPlainText(data).length;
};

interface FeaturedSchoolEditorArgs {
  onSave?: (data: OutputData) => void;
  savedData?: OutputData;
}

interface LpnAdditionalInfoEditorArgs {
  onSave?: (data: OutputData, lpn: LpnPage) => void;
  savedData?: OutputData;
  lpn: LpnPage;
}

class FeaturedSchoolEditor extends Component<FeaturedSchoolEditorArgs> {
  editor!: EditorJS;
  editorInitialized = false;
  @tracked editorData: OutputData | null = null;
  @tracked isReadOnly: boolean = false;
  @tracked characterCount = getCharacterCount(this.args.savedData);
  @tracked saveErrorMessage: string | null = null;

  get remainingCharacters() {
    return MAX_CHAR_COUNT - this.characterCount;
  }

  get isOverLimit() {
    return this.characterCount > MAX_CHAR_COUNT;
  }

  updateCharacterCount = (outputData: OutputData) => {
    this.characterCount = getCharacterCount(outputData);
  };

  save = () => {
    this.editor.save().then((outputData) => {
      this.updateCharacterCount(outputData);
      if (!this.isOverLimit) {
        this.saveErrorMessage = null;
        this.args.onSave?.(outputData);
      } else {
        this.saveErrorMessage = `Save failed: limit is ${MAX_CHAR_COUNT} characters.`;
      }
    });
  };

  // TODO: Return to this. Seems weird to tie the component to the modifier so explicitly.
  initializeEditor = modifier(() => {
    // Guard against double initialization
    if (this.editorInitialized) {
      return;
    }
    this.editorInitialized = true;
    // Ensure we have valid data structure for EditorJS
    const initialData = this.args.savedData?.blocks?.length ? this.args.savedData : undefined;

    this.editor = new EditorJS({
      data: initialData,
      tools: {
        header: Header,
        list: {
          class: List as unknown as ToolConstructable,
          toolbox: [
            {
              title: 'Ordered List',
              data: {
                style: 'ordered',
              },
            },
            {
              title: 'Unordered List',
              data: {
                style: 'unordered',
              },
            },
          ],
        },
      },
      onChange: () => {
        this.save();
      },
      onReady: () => {
        this.updateCharacterCount(initialData || { blocks: [] });
      },
    });
  });

  <template>
    <div
      class="w-full border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800"
    >
      <div class="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
        <h2 class="text-lg font-semibold text-gray-600 dark:text-gray-300">{{t
            "common.featured_school_editor.add_description"
          }}</h2>
        <div class="p-4">
          <p class="text-gray-600 dark:text-gray-300">{{t
              "common.featured_school_editor.visibility_warning"
            }}</p>
        </div>
        <div class="px-4 pb-2 text-xs {{if this.isOverLimit 'text-red-600' 'text-gray-500'}}">
          {{this.remainingCharacters}}
          {{t "common.featured_school_editor.characters_left"}}
        </div>
        {{#if this.saveErrorMessage}}
          <div class="px-4 pb-2 text-xs text-red-600">
            {{this.saveErrorMessage}}
          </div>
        {{/if}}
      </div>
      <div class="px-4 py-2 bg-white dark:bg-gray-900 rounded-b-lg">
        <div id="editorjs" class="mx-12" {{this.initializeEditor}}></div>
      </div>
    </div>
  </template>
}

class LpnAdditionalInfoEditor extends Component<LpnAdditionalInfoEditorArgs> {
  editor!: EditorJS;
  editorInitialized = false;
  @tracked characterCount = getCharacterCount(this.args.savedData);
  @tracked saveErrorMessage: string | null = null;

  get remainingCharacters() {
    return MAX_CHAR_COUNT - this.characterCount;
  }

  get isOverLimit() {
    return this.characterCount > MAX_CHAR_COUNT;
  }

  updateCharacterCount = (outputData: OutputData) => {
    this.characterCount = getCharacterCount(outputData);
  };

  saveAdditionalInfo = () => {
    this.editor.save().then((outputData) => {
      this.updateCharacterCount(outputData);
      if (!this.isOverLimit) {
        this.saveErrorMessage = null;
        this.args.onSave?.(outputData, this.args.lpn);
      } else {
        this.saveErrorMessage = `Save failed: limit is ${MAX_CHAR_COUNT} characters.`;
      }
    });
  };

  initializeEditor = modifier(() => {
    // Guard against double initialization
    if (this.editorInitialized) {
      return;
    }
    this.editorInitialized = true;

    // Ensure we have valid data structure for EditorJS
    const initialData = this.args.savedData?.blocks?.length ? this.args.savedData : undefined;

    this.editor = new EditorJS({
      holder: `editorjs-${this.args.lpn.id}`,
      data: initialData,
      tools: {
        header: Header,
        list: List,
      },
      onChange: () => {
        this.editor.save().then((outputData) => {
          this.args.onSave?.(outputData, this.args.lpn);
          this.updateCharacterCount(outputData);
        });
      },
      onReady: () => {
        this.updateCharacterCount(this.args.savedData || { blocks: [] });
      },
    });
  });

  <template>
    <div
      class="w-full border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800"
    >
      <div class="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
        <p class="!mb-0 text-lg font-semibold text-gray-600 dark:text-gray-300">
          {{t "common.lpn_additional_info_editor.add_additional_information"}}
        </p>
        <div class="pt-1 text-xs {{if this.isOverLimit 'text-red-600' 'text-gray-500'}}">
          {{this.remainingCharacters}}
          {{t "common.lpn_additional_info_editor.characters_left"}}
        </div>
        {{#if this.saveErrorMessage}}
          <div class="pt-1 text-xs text-red-600">
            {{this.saveErrorMessage}}
          </div>
        {{/if}}
      </div>
      <div class="h-[250px] px-4 py-2 bg-white dark:bg-gray-900 rounded-b-lg">
        <div id="editorjs-{{@lpn.id}}" class="mx-12" {{this.initializeEditor}}>
        </div>
      </div>
    </div>
  </template>
}
export { FeaturedSchoolEditor, LpnAdditionalInfoEditor };
